/**
 * Global deck cards state for cross-deck features
 *
 * Uses flat collection pattern: users/{uid}/deck_cards with deck_id field.
 * This enables single-subscription sync for all deck cards.
 *
 * Features enabled:
 * - Cross-deck usage queries ("In X decks")
 * - Deck needs calculations (cards needed across all decks)
 * - Simplified active deck sync (filter from this atom)
 * - Full Scryfall data population via useCardCachePopulator
 * - Derived color identity per deck
 */
import { atom } from 'jotai';

import { createStableSet } from '@/lib/atomUtils';
import { classifyCard } from '@/lib/cardClassification';
import { getDisplayImageUris, getFullOracleText, getManaSourceColors, getOracleId, getTypeLine } from '@/lib/cardUtils';
import { cardIndexByIdAtom } from '@/services/cardDatabase';
import { collectionOwnedByNameAtom, deckUsageByNameAtom } from '@/state/cardAvailability';
import { fullCardCacheAtom } from '@/state/cardCache';
import { cardClassificationByOracleIdAtom, getClassificationForCard } from '@/state/cardClassification';
import type { Color } from '@/types/card';
import type { DeckCard, EnrichedDeckCard } from '@/types/deck';

// ============================================================================
// State
// ============================================================================

export interface AllDeckCardsState {
  /** All deck cards across all user's decks */
  data: DeckCard[] | undefined;
  isLoading: boolean;
  error: Error | null;
}

/** Global atom holding all deck cards across all decks */
export const allDeckCardsAtom = atom<AllDeckCardsState>({
  data: undefined,
  isLoading: true,
  error: null,
});

// ============================================================================
// Derived Atoms
// ============================================================================

/** Cards grouped by deck ID for quick lookup */
export const deckCardsByDeckIdAtom = atom((get) => {
  const { data } = get(allDeckCardsAtom);
  if (!data) return new Map<string, DeckCard[]>();

  const byDeck = new Map<string, DeckCard[]>();
  for (const card of data) {
    const existing = byDeck.get(card.deck_id);
    if (existing) {
      existing.push(card);
    } else {
      byDeck.set(card.deck_id, [card]);
    }
  }
  return byDeck;
});

/** Unique Scryfall IDs across all deck cards (for cache population) */
export const allDeckCardScryfallIdsAtom = atom((get) => {
  const { data } = get(allDeckCardsAtom);
  if (!data) return new Set<string>();
  return new Set(data.map((c) => c.scryfall_id));
});

/**
 * All deck cards enriched with full Scryfall data and ownership fields.
 * Cards without cached data are filtered out (non-nullable card).
 * Ownership follows the shared availability model in `state/cardAvailability`:
 * owned by name, minus copies committed to other *active* decks.
 *
 * Note: Fallback to MinimalCard is NOT done here to avoid subscribing the
 * entire card index (~300k cards) for a rare fallback case. Components
 * should use useCardLookup().getCard() when they need display data for
 * cards not yet in the full cache.
 */
export const enrichedDeckCardsAtom = atom<EnrichedDeckCard[]>((get) => {
  const { data } = get(allDeckCardsAtom);
  const fullCardCache = get(fullCardCacheAtom);
  const ownedByNameLower = get(collectionOwnedByNameAtom);
  const usageByName = get(deckUsageByNameAtom);
  const classificationCache = get(cardClassificationByOracleIdAtom);

  if (!data) return [];

  // Build enriched array — filter out cards without cached data
  // First: compute color identity per deck for fetch land detection
  const colorsByDeckId = new Map<string, Color[]>();
  {
    const colorsSetByDeck = new Map<string, Set<Color>>();
    for (const deckCard of data) {
      const card = fullCardCache.get(deckCard.scryfall_id);
      if (!card?.color_identity) continue;
      let colors = colorsSetByDeck.get(deckCard.deck_id);
      if (!colors) {
        colors = new Set<Color>();
        colorsSetByDeck.set(deckCard.deck_id, colors);
      }
      for (const c of card.color_identity) {
        colors.add(c);
      }
    }
    for (const [deckId, colors] of colorsSetByDeck) {
      colorsByDeckId.set(deckId, Array.from(colors));
    }
  }

  const enriched: EnrichedDeckCard[] = [];
  for (const deckCard of data) {
    const card = fullCardCache.get(deckCard.scryfall_id);
    if (!card) continue; // Filter out null cards

    const nameLower = card.name_lower;
    const ownedQuantity = ownedByNameLower.get(nameLower) ?? 0;

    // Only copies committed to *other active* decks are unavailable — brewing,
    // completed and archived decks never reserve a physical copy.
    let inOtherDecks = 0;
    const committed = usageByName.get(nameLower)?.committed;
    if (committed) {
      for (const [deckId, qty] of committed) {
        if (deckId !== deckCard.deck_id) {
          inOtherDecks += qty;
        }
      }
    }

    const oracleId = getOracleId(card);
    const classification = getClassificationForCard(classificationCache, oracleId) ?? classifyCard(card);
    const cardIsLand = classification.is_land;
    const typeLine = getTypeLine(card) ?? '';
    const deckColors = colorsByDeckId.get(deckCard.deck_id);
    const oracleText = getFullOracleText(card);
    const landColors = cardIsLand ? getManaSourceColors(oracleText, card.produced_mana, typeLine, deckColors) : [];
    const manaSourceColors = getManaSourceColors(oracleText, card.produced_mana, cardIsLand ? typeLine : undefined, deckColors);

    enriched.push({
      ...deckCard,
      card,
      display_image_uris: getDisplayImageUris(card),
      classification,
      land_colors: landColors,
      mana_source_colors: manaSourceColors,
      owned_quantity: ownedQuantity,
      in_other_decks: inOtherDecks,
      available: ownedQuantity - inOtherDecks,
    });
  }

  return enriched;
});

// ============================================================================
// Deck Membership Atoms (for In Deck/Not in Deck filters)
// ============================================================================

/**
 * Set of oracle IDs that appear in any deck.
 * Used for "In Deck" / "Not in Deck" filters in collection/set views.
 * Uses stable reference to prevent cascading re-renders.
 */
const stabilizeDeckOracleIds = createStableSet<string>();
export const cardOracleIdsInDecksAtom = atom((get) => {
  const { data } = get(allDeckCardsAtom);
  const cardIndexById = get(cardIndexByIdAtom);

  if (!data) return stabilizeDeckOracleIds(new Set<string>());

  const oracleIds = new Set<string>();
  for (const card of data) {
    const minimalCard = cardIndexById.get(card.scryfall_id);
    if (minimalCard?.oracle_id) {
      oracleIds.add(minimalCard.oracle_id);
    }
  }
  return stabilizeDeckOracleIds(oracleIds);
});

/**
 * Map of deckId → Set of oracle IDs in that deck.
 * Used for filtering cards by specific deck membership.
 */
export const cardOracleIdsByDeckAtom = atom((get) => {
  const cardsByDeck = get(deckCardsByDeckIdAtom);
  const cardIndexById = get(cardIndexByIdAtom);

  const result = new Map<string, Set<string>>();
  for (const [deckId, cards] of cardsByDeck) {
    const oracleIds = new Set<string>();
    for (const card of cards) {
      const minimalCard = cardIndexById.get(card.scryfall_id);
      if (minimalCard?.oracle_id) {
        oracleIds.add(minimalCard.oracle_id);
      }
    }
    result.set(deckId, oracleIds);
  }
  return result;
});

// ============================================================================
// Color Identity Derived Atoms
// ============================================================================

/** WUBRG color order for sorting */
const COLOR_ORDER: Color[] = ['W', 'U', 'B', 'R', 'G'];

/**
 * Derived color identity for each deck.
 * Computed from deck cards + full card cache (preferred) or card index (fallback).
 * Sorted in WUBRG order.
 *
 * Note: Uses inline fallback pattern instead of useCardLookup() because:
 * - Derived atoms cannot call hooks
 * - Color identity is needed during initial load for deck list UI
 * - The card index subscription is acceptable here since it's loaded once at startup
 */
export const deckColorIdentitiesAtom = atom((get) => {
  const { data: deckCards } = get(allDeckCardsAtom);
  const fullCardCache = get(fullCardCacheAtom);
  const cardIndexById = get(cardIndexByIdAtom);

  if (!deckCards) {
    return new Map<string, Color[]>();
  }

  // Group cards by deck, then compute combined color identity
  const colorsByDeck = new Map<string, Set<Color>>();

  for (const deckCard of deckCards) {
    // Prefer full card cache, fall back to card index
    const card = fullCardCache.get(deckCard.scryfall_id) ?? cardIndexById.get(deckCard.scryfall_id);
    if (!card) continue;

    let colors = colorsByDeck.get(deckCard.deck_id);
    if (!colors) {
      colors = new Set<Color>();
      colorsByDeck.set(deckCard.deck_id, colors);
    }

    for (const color of card.color_identity) {
      colors.add(color);
    }
  }

  // Convert to sorted arrays in WUBRG order
  const result = new Map<string, Color[]>();
  for (const [deckId, colors] of colorsByDeck) {
    result.set(
      deckId,
      COLOR_ORDER.filter((c) => colors.has(c)),
    );
  }

  return result;
});
